import api from '../config/api';

/**
 * Check if a file is an Apple HEIC / HEIF image
 * @param {File} file 
 * @returns {boolean}
 */
export function isHeicFile(file) {
  if (!file) return false;
  const fileName = (file.name || '').toLowerCase();
  const fileType = (file.type || '').toLowerCase();

  return (
    fileName.endsWith('.heic') ||
    fileName.endsWith('.heif') ||
    fileType === 'image/heic' ||
    fileType === 'image/heif' ||
    fileType === 'image/heic-sequence' ||
    fileType === 'image/heif-sequence'
  );
}

/**
 * Check if a file is an image (including HEIC/HEIF) regardless of browser MIME reporting
 * @param {File} file 
 * @returns {boolean}
 */
export function isImageFile(file) {
  if (!file) return false;
  const fileName = (file.name || '').toLowerCase();
  const fileType = (file.type || '').toLowerCase();

  return (
    fileType.startsWith('image/') ||
    /\.(heic|heif|jpg|jpeg|png|gif|webp|bmp|svg|tiff)$/i.test(fileName)
  );
}

/**
 * Check if a file is a video
 * @param {File} file 
 * @returns {boolean}
 */
export function isVideoFile(file) {
  if (!file) return false;
  const fileName = (file.name || '').toLowerCase();
  const fileType = (file.type || '').toLowerCase();

  return (
    fileType.startsWith('video/') ||
    /\.(mp4|mov|avi|webm|mkv|m4v|3gp)$/i.test(fileName)
  );
}

/**
 * Convert Apple HEIC/HEIF file to standard browser-compatible JPEG File object using bundled heic2any
 * @param {File} file 
 * @returns {Promise<File>}
 */
export async function convertHeicToJpeg(file) {
  console.log(`🍏 HEIC/HEIF file detected (${file.name}). Converting to JPEG...`);
  try {
    let heic2any;
    try {
      const module = await import('heic2any');
      heic2any = module.default || module;
    } catch (importErr) {
      if (typeof window !== 'undefined' && window.heic2any) {
        heic2any = window.heic2any;
      } else {
        throw importErr;
      }
    }

    const conversionResult = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.90
    });

    const convertedBlob = Array.isArray(conversionResult) ? conversionResult[0] : conversionResult;
    const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');

    const jpegFile = new File([convertedBlob], newFileName, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });

    console.log(`✅ HEIC converted to JPEG successfully: ${file.name} -> ${jpegFile.name} (${(jpegFile.size / (1024 * 1024)).toFixed(2)} MB)`);
    return jpegFile;
  } catch (error) {
    console.error('⚠️ Could not convert HEIC to JPEG:', error.message);
    return file;
  }
}

/**
 * Get safe MIME type from file object or filename extension
 * @param {File} file 
 * @returns {string}
 */
export function getMimeType(file) {
  if (!file) return 'application/octet-stream';
  if (file.type && file.type !== 'application/octet-stream') {
    return file.type;
  }
  const fileName = (file.name || '').toLowerCase();
  if (fileName.endsWith('.mp4') || fileName.endsWith('.m4v')) return 'video/mp4';
  if (fileName.endsWith('.mov')) return 'video/quicktime';
  if (fileName.endsWith('.webm')) return 'video/webm';
  if (fileName.endsWith('.avi')) return 'video/avi';
  if (fileName.endsWith('.mkv')) return 'video/x-matroska';
  if (fileName.endsWith('.3gp')) return 'video/3gpp';
  if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg')) return 'image/jpeg';
  if (fileName.endsWith('.png')) return 'image/png';
  if (fileName.endsWith('.gif')) return 'image/gif';
  if (fileName.endsWith('.webp')) return 'image/webp';
  if (fileName.endsWith('.svg')) return 'image/svg+xml';
  return isVideoFile(file) ? 'video/mp4' : 'image/jpeg';
}

/**
 * Delete a media file from S3 storage via backend endpoint
 * @param {string} fileUrlOrKey - URL or S3 key of the file to delete
 * @returns {Promise<{success: boolean}>}
 */
export async function deleteMediaFile(fileUrlOrKey) {
  if (!fileUrlOrKey) return { success: true };
  try {
    let key = fileUrlOrKey;
    if (key.startsWith('http://') || key.startsWith('https://')) {
      try {
        const parsed = new URL(key);
        key = parsed.pathname.replace(/^\/+/, '');
      } catch (e) {
        // use as is
      }
    }
    console.log(`🗑️ deleteMediaFile calling backend for key: ${key}`);
    const response = await api.delete(`/upload/${encodeURIComponent(key)}`);
    if (response.ok) {
      const data = await response.json();
      console.log('✅ deleteMediaFile success:', data);
      return data;
    }
  } catch (error) {
    console.warn('⚠️ Could not delete media file from storage:', error.message);
  }
  return { success: false };
}

/**
 * Upload a media file using S3 Presigned URL first (bypasses Lambda 6MB limits & payload issues),
 * falling back to direct multipart backend upload if presigned URL is unavailable.
 * Automatically converts Apple HEIC/HEIF photos to universal JPEG format.
 *
 * @param {File} file - File object to upload
 * @param {string} folder - Target folder in S3 (default: 'general')
 * @param {string|null} customEndpoint - Optional fallback endpoint
 * @returns {Promise<{success: boolean, fileUrl: string, url: string, imageUrl: string, mediaUrl: string, fileName: string, originalName: string, size: number, mimetype: string, data: object}>}
 */
export async function uploadMediaFile(file, folder = 'general', customEndpoint = null) {
  if (!file) {
    throw new Error('No file provided');
  }

  // Auto-convert Apple HEIC/HEIF images to standard JPEG before upload
  if (isHeicFile(file)) {
    try {
      file = await convertHeicToJpeg(file);
    } catch (e) {
      console.warn('HEIC conversion skipped, attempting direct upload:', e.message);
    }
  }

  const fileType = getMimeType(file);
  console.log(`🚀 uploadMediaFile starting for ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB), type: ${fileType}, folder: ${folder}`);

  // Method 1: S3 Presigned URL (Direct browser to S3 upload - optimal for AWS Lambda & Amplify)
  try {
    const presignedRes = await api.post('/upload/presigned', {
      fileName: file.name,
      fileType: fileType,
      folder: folder
    });

    if (presignedRes.ok) {
      const presignedData = await presignedRes.json();
      if (presignedData.success && presignedData.data?.uploadUrl) {
        const { uploadUrl, fileUrl, fileName, mimetype } = presignedData.data;

        console.log('📤 Uploading directly to S3 presigned URL:', uploadUrl.split('?')[0]);
        const s3UploadRes = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': fileType,
          },
          body: file,
        });

        if (s3UploadRes.ok) {
          console.log('✅ Direct S3 upload successful:', fileUrl);
          const resultData = {
            success: true,
            fileName: fileName || file.name,
            fileUrl,
            url: fileUrl,
            imageUrl: fileUrl,
            mediaUrl: fileUrl,
            originalName: file.name,
            size: file.size,
            mimetype: mimetype || fileType,
            data: {
              fileUrl,
              url: fileUrl,
              imageUrl: fileUrl,
              mediaUrl: fileUrl,
              fileName: fileName || file.name,
              originalName: file.name,
              size: file.size,
              mimetype: mimetype || fileType
            }
          };
          return resultData;
        } else {
          console.warn(`⚠️ S3 direct PUT returned status ${s3UploadRes.status}, attempting fallback upload...`);
        }
      }
    }
  } catch (presignedErr) {
    console.warn('⚠️ Presigned URL upload failed:', presignedErr.message);
  }

  // Method 2: Fallback to direct backend upload via multipart form-data
  console.log('🔄 Falling back to backend multipart form-data upload...');
  const formData = new FormData();
  formData.append('file', file);
  formData.append('media', file);
  formData.append('image', file);
  formData.append('logo', file);
  formData.append('photo', file);
  formData.append('folder', folder);

  let endpoint = customEndpoint || '/upload/single';
  if (!endpoint.startsWith('/')) {
    endpoint = '/' + endpoint;
  }

  const response = await api.upload(endpoint, formData);

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ Upload failed with status:', response.status, 'Error:', errorText);
    throw new Error(`Upload failed (${response.status}): ${errorText}`);
  }

  const result = await response.json();
  console.log('📋 Backend upload result:', result);

  if (result.success || result.data || result.fileUrl || result.url) {
    const data = result.data || result;
    const finalUrl = data.fileUrl || data.url || data.imageUrl || data.mediaUrl || result.fileUrl || result.url;

    return {
      success: true,
      fileUrl: finalUrl,
      url: finalUrl,
      imageUrl: finalUrl,
      mediaUrl: finalUrl,
      fileName: data.fileName || file.name,
      originalName: file.name,
      size: file.size,
      mimetype: fileType,
      data: {
        ...data,
        fileUrl: finalUrl,
        url: finalUrl,
        imageUrl: finalUrl,
        mediaUrl: finalUrl
      }
    };
  } else {
    throw new Error(result.error || 'Upload failed');
  }
}

export default uploadMediaFile;
